import {
  Alert,
  FieldContainer,
  Filter,
  Modal,
  Table,
} from '@odin-labs/components';
import {
  ConnectorStatus,
  useGetConnectorRunsQuery,
  useSetConnectorEnabledMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import {
  ArrowUpRightFromSquare,
  LinkIcon,
  PauseIcon,
  PlayIcon,
} from 'components/icons';
import { LoadingError } from 'components/loadingError';
import type { LoadingErrorProps } from 'components/loadingError';
import { loggingConfig } from 'config/logging';
import {
  type ConnectorRunFilters,
  getRunColumns,
  getRunFilterItems,
} from 'containers/jobsiteConfiguration/tabs/connectors/drawers/ConnectorDetailsDrawer.tables';
import type { ConnectorDetailsFieldsFragment } from 'apollo/generated/client-operations';
import type { ConnectorDetailsDrawerCommonProps } from 'containers/jobsiteConfiguration/tabs/connectors/drawers/connectorCatalogEntry';
import React from 'react';
import { smallTablePaginationSizePerPage } from 'utils/constants';
import { getFriendlyFormattedDateTime } from 'utils/dates';
import { getGraphQLError } from 'utils/error';

export type ConnectorDetailsPresentation = {
  title: string;
  description: string;
  setting?: {
    label: string;
    name?: string | null;
    url?: string | null;
  };
  statusMessages: {
    draft: string;
    disabled: string;
    connectionFailed: string;
    connecting: string;
    connected: string;
    enabling: string;
    enablementFailed: string;
  };
};

export type ConnectorConnectionRecoveryAction = {
  loading: boolean;
  execute: () => Promise<unknown>;
  successTitle: string;
  failureTitle: string;
};

type ConnectorDetailsDrawerLayoutProps = ConnectorDetailsDrawerCommonProps & {
  connector: ConnectorDetailsFieldsFragment;
  presentation: ConnectorDetailsPresentation;
  connectionRecovery: ConnectorConnectionRecoveryAction;
  onRefreshDetails: () => Promise<unknown>;
};

type ConnectorRunHistoryState = {
  pageIndex: number;
  filters: ConnectorRunFilters;
};

const initialRunHistoryState = (): ConnectorRunHistoryState => ({
  pageIndex: 0,
  filters: {
    statuses: null,
    dateRange: null,
  },
});

export function ConnectorDetailsDrawerQueryState({
  open,
  title,
  onClose,
  onAfterLeave,
  loading,
  error,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onAfterLeave: () => void;
  loading: boolean;
  error?: LoadingErrorProps['error'];
}): React.ReactElement {
  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => !nextOpen && onClose()}
      afterLeave={onAfterLeave}
      variant="drawer"
      size="md"
      title={title}
      actionsPanel={null}
      onCancel={onClose}
      cancelText="Close"
    >
      <LoadingError loading={loading} error={error} />
    </Modal>
  );
}

function DetailField({
  name,
  label,
  value,
}: {
  name: string;
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <FieldContainer
      name={name}
      label={label}
      className="odin-col-span-6 sm:odin-col-span-3"
    >
      <p className="odin-break-all odin-text-sm odin-leading-relaxed odin-text-gray-700">
        {value == null || value === '' ? 'None' : value}
      </p>
    </FieldContainer>
  );
}

function AuditDetails({
  connector,
}: {
  connector: ConnectorDetailsFieldsFragment;
}): React.ReactElement {
  return (
    <div className="odin-space-y-1 odin-text-1.5xs odin-leading-5 odin-text-odin-dark-gray">
      <p>
        Created on {getFriendlyFormattedDateTime(connector.createdAt)} by{' '}
        {connector.createdBy.name}
      </p>
      <p>
        Updated on {getFriendlyFormattedDateTime(connector.updatedAt)} by{' '}
        {connector.updatedBy.name}
      </p>
    </div>
  );
}

export function ConnectorDetailsDrawerLayout({
  connector,
  open,
  onClose,
  onAfterLeave,
  onUpdated,
  onRefreshDetails,
  presentation,
  connectionRecovery,
}: ConnectorDetailsDrawerLayoutProps): React.ReactElement {
  const [setConnectorEnabled, setConnectorEnabledState] =
    useSetConnectorEnabledMutation();
  const [runHistoryState, setRunHistoryState] =
    React.useState<ConnectorRunHistoryState>(initialRunHistoryState);
  const { pageIndex: runsPageIndex, filters: runFilters } = runHistoryState;
  const runsQueryFilter = React.useMemo(() => {
    const active = Boolean(
      runFilters.statuses?.length ||
        runFilters.dateRange?.startDate ||
        runFilters.dateRange?.endDate,
    );
    return active
      ? {
          statuses: runFilters.statuses?.length
            ? runFilters.statuses
            : undefined,
          startDate: runFilters.dateRange?.startDate
            ?.clone()
            .startOf('day')
            .toDate(),
          endDate: runFilters.dateRange?.endDate?.clone().endOf('day').toDate(),
        }
      : null;
  }, [runFilters]);
  const runFilterActive = runsQueryFilter !== null;
  const runsQuery = useGetConnectorRunsQuery({
    variables: {
      connectorId: connector.id,
      limit: smallTablePaginationSizePerPage,
      offset: runsPageIndex * smallTablePaginationSizePerPage,
      filter: runsQueryFilter,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !open,
  });
  const runsPage = runsQuery.data?.connector?.runs;
  const runColumns = React.useMemo(() => getRunColumns(), []);
  const runFilterItems = React.useMemo(() => getRunFilterItems(), []);

  const refreshConnectorData = async (
    includeRunHistory: boolean,
  ): Promise<void> => {
    const refreshes: Array<{
      target: string;
      run: () => Promise<unknown>;
    }> = [
      { target: 'connector list', run: onUpdated },
      { target: 'connector details', run: onRefreshDetails },
    ];
    if (includeRunHistory)
      refreshes.push({ target: 'run history', run: runsQuery.refetch });

    const results = await Promise.allSettled(
      refreshes.map(({ run }) => Promise.resolve().then(run)),
    );
    results.forEach((result, index) => {
      if (result.status !== 'rejected') return;
      const error =
        result.reason instanceof Error
          ? {
              message: result.reason.message,
              kind: result.reason.name,
              stack: result.reason.stack,
            }
          : result.reason;
      loggingConfig.logger?.warn('Could not refresh connector data', {
        target: refreshes[index].target,
        error,
      });
    });
  };

  const runConnectorAction = async ({
    mutate,
    successTitle,
    failureTitle,
    refreshRunHistory = false,
  }: {
    mutate: () => Promise<unknown>;
    successTitle: string;
    failureTitle: string;
    refreshRunHistory?: boolean;
  }): Promise<void> => {
    try {
      await mutate();
      AlertService.alert('success', successTitle, null);
    } catch (error) {
      AlertService.alert('danger', failureTitle, getGraphQLError(error));
    }
    await refreshConnectorData(refreshRunHistory);
  };

  const onEnabledChange = async (enabled: boolean): Promise<void> => {
    await runConnectorAction({
      mutate: () =>
        setConnectorEnabled({
          variables: { connectorId: connector.id, enabled },
        }),
      successTitle: `Connector ${enabled ? 'enabled' : 'disabled'}`,
      failureTitle: `Could not ${enabled ? 'enable' : 'disable'} connector`,
      refreshRunHistory: enabled,
    });
  };

  const onRecovery = async (): Promise<void> => {
    await runConnectorAction({
      mutate: connectionRecovery.execute,
      successTitle: connectionRecovery.successTitle,
      failureTitle: connectionRecovery.failureTitle,
      refreshRunHistory: true,
    });
  };

  const onRunFiltersChange = (
    changedFilters: Partial<ConnectorRunFilters>,
  ): void => {
    setRunHistoryState((current) => ({
      pageIndex: 0,
      filters: { ...current.filters, ...changedFilters },
    }));
  };

  const actionLoading =
    setConnectorEnabledState.loading || connectionRecovery.loading;
  let actionText = 'Enable';
  let actionIcon = PlayIcon;
  let actionButtonTheme: 'danger' | 'primary' = 'primary';
  let actionEnabled = !actionLoading;
  let onAction = (): Promise<void> => onEnabledChange(true);
  switch (connector.status) {
    case ConnectorStatus.Draft:
      actionText = 'Connect';
      actionIcon = LinkIcon;
      onAction = onRecovery;
      break;
    case ConnectorStatus.ConnectionFailed:
      actionText = 'Try again';
      actionIcon = LinkIcon;
      onAction = onRecovery;
      break;
    case ConnectorStatus.Connecting:
      actionText = 'Connecting';
      actionEnabled = false;
      break;
    case ConnectorStatus.Connected:
      break;
    case ConnectorStatus.Enabling:
      actionText = 'Enabling';
      actionEnabled = false;
      break;
    case ConnectorStatus.EnablementFailed:
      actionText = 'Try again';
      break;
    case ConnectorStatus.Enabled:
      actionText = 'Disable';
      actionIcon = PauseIcon;
      actionButtonTheme = 'danger';
      onAction = (): Promise<void> => onEnabledChange(false);
      break;
    case ConnectorStatus.Disabled:
      break;
    default: {
      const unsupportedStatus: never = connector.status;
      throw new Error(`Unsupported Connector status: ${unsupportedStatus}`);
    }
  }

  let disabledAlertType: 'danger' | 'info' = 'info';
  let disabledAlertText: string;
  switch (connector.status) {
    case ConnectorStatus.Draft:
      disabledAlertText = presentation.statusMessages.draft;
      break;
    case ConnectorStatus.Connecting:
      disabledAlertText = presentation.statusMessages.connecting;
      break;
    case ConnectorStatus.ConnectionFailed:
      disabledAlertType = 'danger';
      disabledAlertText = presentation.statusMessages.connectionFailed;
      break;
    case ConnectorStatus.Connected:
      disabledAlertText = presentation.statusMessages.connected;
      break;
    case ConnectorStatus.Enabling:
      disabledAlertText = presentation.statusMessages.enabling;
      break;
    case ConnectorStatus.EnablementFailed:
      disabledAlertType = 'danger';
      disabledAlertText = presentation.statusMessages.enablementFailed;
      break;
    case ConnectorStatus.Disabled:
      disabledAlertText = presentation.statusMessages.disabled;
      break;
    case ConnectorStatus.Enabled:
      disabledAlertText = '';
      break;
    default: {
      const unsupportedStatus: never = connector.status;
      throw new Error(`Unsupported Connector status: ${unsupportedStatus}`);
    }
  }

  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => !nextOpen && onClose()}
      afterLeave={onAfterLeave}
      variant="drawer"
      size="md"
      title={presentation.title}
      onAction={onAction}
      actionText={actionText}
      actionButtonTheme={actionButtonTheme}
      actionIcon={actionIcon}
      actionButtonEnabled={actionEnabled}
      actionButtonWithSpinner={actionLoading}
      onCancel={onClose}
      cancelText="Close"
      cancelButtonEnabled={!actionLoading}
      overlayCloseDisabled={actionLoading}
    >
      <div className="odin-space-y-10">
        <div className="odin-space-y-4">
          <p className="odin-max-w-xl odin-text-sm odin-leading-6 odin-text-gray-600">
            {presentation.description}
          </p>
          {connector.status !== ConnectorStatus.Enabled && (
            <Alert type={disabledAlertType} text={disabledAlertText} />
          )}
        </div>

        {presentation.setting && (
          <div className="odin-grid odin-grid-cols-6 odin-gap-6">
            <DetailField
              name="connectorSetting"
              label={presentation.setting.label}
              value={
                presentation.setting.url ? (
                  <a
                    href={presentation.setting.url}
                    target="_blank"
                    rel="noreferrer"
                    className="odin-inline-flex odin-items-center odin-gap-2 odin-font-medium odin-text-odin-primary hover:odin-underline"
                  >
                    {presentation.setting.name}
                    <ArrowUpRightFromSquare className="odin-h-3.5 odin-w-3.5" />
                  </a>
                ) : (
                  presentation.setting.name
                )
              }
            />
          </div>
        )}

        <section className="odin-space-y-4">
          <h3 className="odin-text-base odin-font-medium odin-text-gray-900">
            Run history
          </h3>
          <div className="odin-space-y-4">
            <Filter
              items={runFilterItems}
              loading={runsQuery.loading}
              onChange={onRunFiltersChange}
            />
            {runsQuery.error ? (
              <LoadingError error={runsQuery.error} />
            ) : (
              <Table
                columns={runColumns}
                data={runsPage?.runs ?? []}
                loading={runsQuery.loading}
                noResultsText={
                  runFilterActive
                    ? 'No runs match these filters'
                    : 'No runs to show'
                }
                initialState={{ pageSize: smallTablePaginationSizePerPage }}
                pageIndex={runsPageIndex}
                pageCount={Math.ceil(
                  (runsPage?.total ?? 0) / smallTablePaginationSizePerPage,
                )}
                remote
                enablePagination
                onPageChange={(_pageSize, pageIndex): void =>
                  setRunHistoryState((current) => ({ ...current, pageIndex }))
                }
                disableGlobalFilter
                disableSortBy
              />
            )}
          </div>
        </section>

        <AuditDetails connector={connector} />
      </div>
    </Modal>
  );
}
